import React from 'react'
import { Search, UserPlus, Settings, LogOut, Building, Zap, Globe } from 'lucide-react'
import { Button, ErrorBanner } from '@/components'
import { useNavigate } from 'react-router-dom'
import deepWindLogo from '@/assets/deepwind-logo.png'
import { useLanguage } from '@/contexts/LanguageContext'
import { useUserManagementViewModel } from './useUserManagementViewModel'
import UserTable from './components/UserTable'
import AddUserModal from './components/AddUserModal'
import EditUserModal from './components/EditUserModal'
import PasswordModal from './components/PasswordModal'
import DeleteUserModal from './components/DeleteUserModal'

const UserManagementScreen: React.FC = () => {
  const navigate = useNavigate()
  const { language, setLanguage, t } = useLanguage()

  const {
    companies,
    loading,
    isSubmitting,
    loadError,
    companiesError,
    addUserError,
    editUserError,
    deleteUserError,
    passwordError,
    searchTerm,
    setSearchTerm,
    currentUser,
    showAddModal,
    showEditModal,
    showDeleteModal,
    showPasswordModal,
    editingUser,
    deletingUser,
    passwordUser,
    showRoleDropdown,
    setShowRoleDropdown,
    showCompanyDropdown,
    setShowCompanyDropdown,
    showEditRoleDropdown,
    setShowEditRoleDropdown,
    showEditCompanyDropdown,
    setShowEditCompanyDropdown,
    newUser,
    setNewUser,
    editUser,
    setEditUser,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    filteredUsers,
    hasAdminAccess,
    hasSuperAdminAccess,
    openAddModal,
    closeAddModal,
    closeEditModal,
    closeDeleteModal,
    closePasswordModal,
    handleAddUser,
    handleEditUser,
    handleUpdateUser,
    handleDeleteUser,
    handleConfirmDelete,
    handleChangePassword,
    handleUpdatePassword,
    handleLogout,
    dismissLoadError,
    dismissCompaniesError,
    dismissAddUserError,
    dismissEditUserError,
    dismissDeleteUserError,
    dismissPasswordError,
    retryLoadUsers,
  } = useUserManagementViewModel()

  // Ekle/Duzenle modallarinda sirket dropdown'u sirket listesine bagli. Liste
  // cekilemediyse bu modallarin hata slotunda da gorunmeli, yoksa kullanici
  // dropdown'un neden bos oldugunu bilemez. Modal'a ozgu hata onceliklidir.
  const addModalError = addUserError ?? companiesError
  const editModalError = editUserError ?? companiesError

  const dismissAddModalError = () => {
    dismissAddUserError()
    dismissCompaniesError()
  }

  const dismissEditModalError = () => {
    dismissEditUserError()
    dismissCompaniesError()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                  <img 
                    src={deepWindLogo} 
                    alt="VentraWind Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">VentraWind</h1>
                  <p className="text-xs text-gray-500">{t.dashboard.userSettings}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Zap className="w-4 h-4 mr-2" />
{t.dashboard.turbineManagement}
              </Button>
              {hasSuperAdminAccess && (
                <Button
                  onClick={() => navigate('/dashboard/companies')}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                >
                  <Building className="w-4 h-4 mr-2" />
                  Partner Yönetimi
                </Button>
              )}
              <Button
                onClick={() => {}}
                className="bg-gray-400 cursor-not-allowed text-white"
                size="sm"
                disabled
              >
                <Settings className="w-4 h-4 mr-2" />
                Kullanıcı Ayarları
              </Button>
              
              {/* Language Toggle Button */}
              <Button
                onClick={() => {
                  const newLanguage = language === 'tr' ? 'en' : 'tr'
                  setLanguage(newLanguage)
                }}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors min-w-[80px]"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'tr' ? 'English' : 'Türkçe'}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">{t.dashboard.userSettings}</h2>
          <p className="text-gray-600">Sistem kullanıcılarını yönetin</p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search */}
            <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            {/* Add User Button - Only for Superadmin */}
            {hasSuperAdminAccess && (
              <Button
                onClick={openAddModal}
                className="bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Yeni Kullanıcı Ekle
              </Button>
            )}
          </div>
          </div>

        {/* Sayfa govdesi hatasi: YALNIZ modal disi (liste/oturum yukleme) hatalar.
            Modal ici hatalar backdrop'un altinda kalmamasi icin modallarin
            kendi `error` slotuna gider. */}
        <ErrorBanner
          message={loadError}
          onDismiss={dismissLoadError}
          /* Oturum okunamadiysa currentUser yok; "Tekrar dene" hicbir sey
             yapamayacagi icin butonu hic gostermiyoruz. */
          onRetry={currentUser ? retryLoadUsers : undefined}
          className="mb-6"
        />

        {/* Users Content */}
        <UserTable
          loading={loading}
          filteredUsers={filteredUsers}
          searchTerm={searchTerm}
          hasAdminAccess={hasAdminAccess}
          currentUser={currentUser}
          handleEditUser={handleEditUser}
          handleChangePassword={handleChangePassword}
          handleDeleteUser={handleDeleteUser}
        />

        {/* Add User Modal */}
        <AddUserModal
          showAddModal={showAddModal}
          onClose={closeAddModal}
          showRoleDropdown={showRoleDropdown}
          setShowRoleDropdown={setShowRoleDropdown}
          showCompanyDropdown={showCompanyDropdown}
          setShowCompanyDropdown={setShowCompanyDropdown}
          newUser={newUser}
          setNewUser={setNewUser}
          companies={companies}
          handleAddUser={handleAddUser}
          error={addModalError}
          onDismissError={dismissAddModalError}
          isSubmitting={isSubmitting}
        />

        {/* Edit User Modal */}
        <EditUserModal
          showEditModal={showEditModal}
          onClose={closeEditModal}
          editingUser={editingUser}
          showEditRoleDropdown={showEditRoleDropdown}
          setShowEditRoleDropdown={setShowEditRoleDropdown}
          showEditCompanyDropdown={showEditCompanyDropdown}
          setShowEditCompanyDropdown={setShowEditCompanyDropdown}
          editUser={editUser}
          setEditUser={setEditUser}
          companies={companies}
          handleUpdateUser={handleUpdateUser}
          error={editModalError}
          onDismissError={dismissEditModalError}
          isSubmitting={isSubmitting}
        />

        {/* Password Change Modal */}
        <PasswordModal
          showPasswordModal={showPasswordModal}
          onClose={closePasswordModal}
          passwordUser={passwordUser}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          handleUpdatePassword={handleUpdatePassword}
          error={passwordError}
          onDismissError={dismissPasswordError}
          isSubmitting={isSubmitting}
        />

        {/* Delete Confirmation Modal */}
        <DeleteUserModal
          showDeleteModal={showDeleteModal}
          onClose={closeDeleteModal}
          deletingUser={deletingUser}
          handleConfirmDelete={handleConfirmDelete}
          error={deleteUserError}
          onDismissError={dismissDeleteUserError}
          isSubmitting={isSubmitting}
        />
      </main>
    </div>
  )
}

export default UserManagementScreen
